import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { ProductCard } from "../components/ProductCard";
import { ArrowRight, ArrowLeft, ArrowUpRight } from "lucide-react";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[80vh] min-h-[560px] w-full overflow-hidden">
        <img
          src="https://images.pexels.com/photos/18082156/pexels-photo-18082156.jpeg"
          alt="Curated picks"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full mx-auto max-w-7xl px-6 md:px-12 flex flex-col justify-end pb-20">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-white/80 mb-4 animate-fade-up">
            The internet's best products, hand-picked
          </div>
          <h1 className="font-display text-5xl sm:text-7xl md:text-8xl tracking-tighter leading-[0.9] font-black uppercase text-white max-w-4xl animate-fade-up">
            Shop smarter.<br />Buy only the best.
          </h1>
          <div className="mt-8 flex flex-wrap gap-4 animate-fade-up">
            <a
              href="#featured"
              data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 bg-[#FF2A00] hover:bg-[#D92400] text-white px-7 py-3.5 font-medium text-sm transition-colors duration-300"
            >
              Explore picks <ArrowRight size={16} />
            </a>
            <Link
              to="/category/gadgets"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 text-white px-7 py-3.5 font-medium text-sm hover:bg-white/20 transition-colors duration-300"
            >
              Browse categories
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-3">Browse by</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Categories</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map((c, i) => (
            <Link
              key={c.id}
              to={`/category/${c.slug}`}
              data-testid={`home-cat-${c.slug}`}
              className={`group relative overflow-hidden border border-zinc-200 hover:border-black transition-all duration-300 ${i === 0 ? "md:col-span-2 md:row-span-1" : ""}`}
            >
              <div className={`overflow-hidden ${i === 0 ? "aspect-[2/1]" : "aspect-square"}`}>
                <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 flex items-center justify-between w-full">
                <span className="font-display text-white text-2xl font-bold uppercase tracking-tight">{c.name}</span>
                <ArrowUpRight className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section id="featured" className="bg-[#F4F4F5] py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A00] mb-3">Editor's choice</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Featured picks</h2>
            </div>
            <div className="hidden md:flex gap-2">
              <button onClick={() => scroll(-1)} data-testid="featured-prev" className="p-3 border border-zinc-300 hover:border-black hover:bg-black hover:text-white transition-colors"><ArrowLeft size={16} /></button>
              <button onClick={() => scroll(1)} data-testid="featured-next" className="p-3 border border-zinc-300 hover:border-black hover:bg-black hover:text-white transition-colors"><ArrowRight size={16} /></button>
            </div>
          </div>
          <div ref={scrollRef} className="flex gap-5 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
            {products.map((p) => (
              <div key={p.id} className="min-w-[280px] w-[280px] flex-shrink-0">
                <ProductCard product={p} />
              </div>
            ))}
            {products.length === 0 && <p className="text-zinc-400">No featured products yet.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
